import type { GameMode, Grid, MathConfig, Rng, SpinBook, WinTier } from '../types';
import { evaluate } from './evaluate';
import { gridFromStops, randomStops } from './reels';

/** Produce the complete outcome of one spin. */
export function spin(config: MathConfig, rng: Rng, bet: number, mode: GameMode): SpinBook {
  return spinWithStops(config, randomStops(config, rng), bet, mode);
}

/** Deterministic book for given stops (tests, replays, forced outcomes). */
export function spinWithStops(config: MathConfig, stops: number[], bet: number, mode: GameMode): SpinBook {
  const grid = gridFromStops(config, stops);
  const r = evaluate(config, grid, bet, mode);
  return {
    mode,
    bet,
    stops: [...stops],
    grid,
    lineWins: r.lineWins,
    scatterWin: r.scatterWin,
    freeSpinsAwarded: r.freeSpinsAwarded,
    multiplier: mode === 'free' ? config.freeSpins.multiplier : 1,
    totalWin: r.totalWin,
    capped: r.capped,
  };
}

/**
 * Random search for a book satisfying `predicate` (debug / bonus buy).
 * Returns the last book tried if nothing matched within maxTries.
 */
export function forceOutcome(
  config: MathConfig,
  rng: Rng,
  bet: number,
  mode: GameMode,
  predicate: (book: SpinBook) => boolean,
  maxTries = 500_000,
): SpinBook {
  let book = spin(config, rng, bet, mode);
  for (let i = 1; i < maxTries && !predicate(book); i++) book = spin(config, rng, bet, mode);
  return book;
}

export function winTier(config: MathConfig, book: SpinBook): WinTier {
  if (book.totalWin <= 0) return 'none';
  const x = book.totalWin / book.bet;
  if (x >= config.winTiers.epic) return 'epic';
  if (x >= config.winTiers.mega) return 'mega';
  if (x >= config.winTiers.big) return 'big';
  return 'normal';
}

/**
 * Index of the reel right after the second scatter (scanning left to right), i.e. the first reel
 * that should spin with anticipation. Null when fewer than two scatters land on reels 0..reels-2
 * or when the second scatter sits on the last reel.
 */
export function anticipationReel(config: MathConfig, grid: Grid): number | null {
  if (!config.scatter) return null;
  let seen = 0;
  for (let reel = 0; reel < config.reels; reel++) {
    if (grid[reel].includes(config.scatter.symbol)) seen++;
    if (seen >= 2) return reel + 1 < config.reels ? reel + 1 : null;
  }
  return null;
}
